import { LayoutCard, Stack } from '@cast/design-system';
import {
  useClusterDashboardMonthlyCostData,
  useClusterDashboardTimeRange,
} from '@cast/features-cluster-shared';

import { clusterDashboardChartColors } from '../_constants/dashboardTokens';
import {
  ChartSummaryMetric,
  ChartSummaryMetricsRow,
} from './shared/ChartSummaryMetricsRow';
import { ClusterDashboardRangePicker } from './shared/ClusterDashboardRangePicker';
import { DashboardChartFrame } from './shared/DashboardChartFrame';
import { DashboardSectionError } from './shared/DashboardSectionError';
import {
  ChartSummaryMetricSkeleton,
  ChartSummaryMetricsRowSkeleton,
} from './shared/DashboardMetricsSkeleton';
import { MonthlyClusterCostChart } from './shared/MonthlyClusterCostChart';

const FALLBACK_TOOLTIP = 'Temporary on-demand node while spot is not available';

export const MonthlyClusterCostSection = () => {
  const { timeZone } = useClusterDashboardTimeRange();
  const {
    chartData,
    currentSpend,
    onDemandSpend,
    spotSpend,
    fallbackSpend,
    isLoading,
    hasError,
    refetch,
  } = useClusterDashboardMonthlyCostData();

  return (
    <LayoutCard
      title="Monthly cluster cost"
      headerEndContent={<ClusterDashboardRangePicker />}
      testId="monthly-cluster-cost-section"
    >
      <DashboardSectionError hasError={hasError} onRetry={refetch}>
        <Stack gap={16}>
          {isLoading ? (
            <ChartSummaryMetricsRowSkeleton>
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
              <ChartSummaryMetricSkeleton />
            </ChartSummaryMetricsRowSkeleton>
          ) : (
            <ChartSummaryMetricsRow>
              <ChartSummaryMetric
                label={currentSpend.label}
                value={currentSpend.value}
                unit={currentSpend.unit}
              />
              <ChartSummaryMetric
                color={clusterDashboardChartColors.onDemand}
                label={onDemandSpend.label}
                value={onDemandSpend.value}
                unit={onDemandSpend.unit}
              />
              <ChartSummaryMetric
                color={clusterDashboardChartColors.spot}
                label={spotSpend.label}
                value={spotSpend.value}
                unit={spotSpend.unit}
              />
              <ChartSummaryMetric
                color={clusterDashboardChartColors.fallback}
                label={fallbackSpend.label}
                value={fallbackSpend.value}
                unit={fallbackSpend.unit}
                infoTooltip={FALLBACK_TOOLTIP}
              />
            </ChartSummaryMetricsRow>
          )}

          <DashboardChartFrame>
            <MonthlyClusterCostChart
              data={chartData}
              timeZone={timeZone}
              isLoading={isLoading}
              hasNoData={!isLoading && !chartData.length}
            />
          </DashboardChartFrame>
        </Stack>
      </DashboardSectionError>
    </LayoutCard>
  );
};
